import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Globe2 } from "lucide-react";
import { toast } from "sonner";
import { api, detectedTimezone, setTimezoneOverride } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ModalClose } from "@/components/ui/ModalClose";

const ASKED_KEY = "hc_tz_asked";
export const allZones = () => (typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [detectedTimezone()]);
const pretty = (tz) => (tz || "").replace(/_/g, " ");
const nowIn = (tz) => { try { return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: tz }); } catch { return ""; } };

export async function saveTimezone(tz, setUser) {
  const r = await api.patch("/me/timezone", { timezone: tz, apply_to_reminders: true });
  setTimezoneOverride(tz);
  setUser?.((u) => (u ? { ...u, timezone: tz } : u));
  return r.data;
}

export function TimezoneSelect({ value, onChange, testid = "tz-select" }) {
  const zones = useMemo(allZones, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-3 py-2.5 rounded-xl border border-stone-300 text-sm bg-white focus:border-amber-600 outline-none">
      {zones.map((z) => <option key={z} value={z}>{pretty(z)}</option>)}
    </select>
  );
}

/** On open: silently adopt the device zone the first time; otherwise ask when the device zone differs from home. */
export function TimezonePrompt() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [pick, setPick] = useState(detectedTimezone());
  const detected = detectedTimezone();
  const home = user?.timezone;

  useEffect(() => {
    if (!user || user.role !== "client") return;
    if (!home) { saveTimezone(detected, setUser).catch(() => {}); return; }
    setTimezoneOverride(home);
    if (home !== detected && sessionStorage.getItem(ASKED_KEY) !== detected) setOpen(true);
  }, [user?.id, home, detected]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !home) return null;
  const close = () => { sessionStorage.setItem(ASKED_KEY, detected); setTimezoneOverride(home); setOpen(false); };
  const apply = async (tz) => {
    try {
      const r = await saveTimezone(tz, setUser);
      toast.success(`Time zone set to ${pretty(tz)}${r.reminders_moved ? ` — ${r.reminders_moved} reminder${r.reminders_moved === 1 ? "" : "s"} moved with you` : ""}.`);
    } catch { toast.error("Couldn't save that time zone."); }
    sessionStorage.setItem(ASKED_KEY, detected);
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" onClick={close} data-testid="timezone-prompt">
      <motion.div className="warm-card relative p-6 sm:p-8 w-full max-w-md" initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()}>
        <ModalClose onClose={close} testid="timezone-close" />
        <div className="flex items-center gap-2 text-amber-800 text-xs font-bold uppercase tracking-widest"><Globe2 className="w-4 h-4" /> Time zone check</div>
        <h3 className="font-display text-2xl font-bold text-stone-900 mt-2">I think you're not in {pretty(home)} right now — is that correct?</h3>
        <p className="text-sm text-stone-600 mt-2">
          Your device says <strong data-testid="tz-detected">{pretty(detected)}</strong> ({nowIn(detected)}). Your home zone is <strong data-testid="tz-home">{pretty(home)}</strong> ({nowIn(home)}).
          This decides when reminders ring and when a new day starts.
        </p>
        {!choosing ? (
          <div className="grid gap-2 mt-5">
            <button onClick={() => apply(detected)} data-testid="tz-yes" className="amber-btn w-full text-sm">Yes — switch to {pretty(detected)}</button>
            <button onClick={close} data-testid="tz-keep-home" className="w-full px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700 hover:border-amber-600">No — keep my home time zone ({pretty(home)})</button>
            <button onClick={() => setChoosing(true)} data-testid="tz-choose-other" className="text-xs font-semibold text-stone-500 hover:text-stone-800 mt-1">Choose another time zone</button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <TimezoneSelect value={pick} onChange={setPick} />
            <div className="flex gap-2">
              <button onClick={() => setChoosing(false)} data-testid="tz-back" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">Back</button>
              <button onClick={() => apply(pick)} data-testid="tz-apply" className="flex-1 amber-btn text-sm">Use {pretty(pick)}</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
